import Phaser from 'phaser';
import { watch, type WatchStopHandle } from 'vue';
import { GameEvents } from '../lib/GameEvents';
import type { EventEmitter } from '../types/Events';
import type { LevelConfig } from '../types/Level';
import type { UiState } from '../lib/GameManager';
import { TUNE } from '../lib/config';
import { computeTurnDots } from '../lib/TurnPatternDots';
import { HexViewCoords, HexCoords, HexBearing } from '../grid';

// Extend Phaser sprite type to include our custom viewCoords property
interface InteractiveSprite extends Phaser.GameObjects.Sprite {
  viewCoords: {
    hexCoords: { q: number; r: number };
  };
}

export default class LevelScene extends Phaser.Scene {
  private eventEmitter: EventEmitter;
  private gameState: Readonly<UiState>;
  private levelConfig: LevelConfig | null = null;

  // Set by SpatialSystem when creating interactive sprites
  downSprite: InteractiveSprite | null = null;

  // Terrain rendering
  private terrainSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private terrainWatcher: WatchStopHandle | null = null;

  // Camera tracking
  private lastPlayerCoords: { q: number; r: number } | null = null;

  // Character rendering
  private characterSprites: Map<string, Phaser.GameObjects.Sprite> = new Map();
  private energyDisplays: Map<string, Phaser.GameObjects.Container> = new Map();
  private playerSpeed: number = 0;
  private animatingEntities: Set<string> = new Set();
  private characterWatcher: WatchStopHandle | null = null;
  private animationWatcher: WatchStopHandle | null = null;

  // Highlight rendering
  private playerActionHighlights: Phaser.GameObjects.Group | null = null;
  private enemyPatternHighlights: Phaser.GameObjects.Group | null = null;
  private intentionIndicators: Phaser.GameObjects.Group | null = null;
  private viewConverter: any = null;
  private highlightWatcher: WatchStopHandle | null = null;
  private intentionWatcher: WatchStopHandle | null = null;

  constructor(eventEmitter: EventEmitter, gameState: Readonly<UiState>) {
    super({ key: 'level' });
    this.eventEmitter = eventEmitter;
    this.gameState = gameState;
  }

  init(data: LevelConfig): void {
    this.levelConfig = data;
    this.lastPlayerCoords = null;
  }

  preload(): void {
    if (!this.levelConfig) return;

    // Dynamically load all image assets from the config
    this.levelConfig.assets.forEach(asset => {
      this.load.image(asset.key, asset.url);
    });
  }

  create(): void {
    this._setupInputEvents();

    // Initialize view converter (matches SpatialSystem parameters)
    const hexLongRadius = 60;
    const xOffset = 60;
    const yOffset = 60;
    this.viewConverter = {
      hexLongRadius,
      xOffset,
      yOffset,
      alpha: 0, // Not used for coordinate math
    };

    // Initialize highlight groups
    this.lastPlayerCoords = null;
    this.playerActionHighlights = this.add.group();
    this.enemyPatternHighlights = this.add.group();
    this.intentionIndicators = this.add.group();

    // Watch terrain reactively. immediate: true ensures we render current state even if
    // LevelManager has already initialized and populated spatial data.
    this.terrainWatcher = watch(
      () => this.gameState.spatial.terrain,
      (terrain) => this._renderTerrain(terrain),
      { deep: true, immediate: true }
    );

    // Watch characters reactively
    this.characterWatcher = watch(
      () => this.gameState.spatial.characters,
      (characters) => this._updateCharacters(characters),
      { deep: true, immediate: true }
    );

    // Watch animations reactively
    this.animationWatcher = watch(
      () => this.gameState.spatial.pendingAnimations,
      (animations) => {
        if (animations) {
          this._playAnimations(animations.plan);
        }
      },
      { immediate: true }
    );

    // Watch spatial highlights reactively
    this.highlightWatcher = watch(
      () => this.gameState.spatial.highlights,
      (highlights) => {
        this._renderPlayerActionHighlights(highlights.playerActions);
        this._renderEnemyPatternHighlights(highlights.enemyPattern);
      },
      { deep: true, immediate: true }
    );

    // Watch intention indicators reactively
    this.intentionWatcher = watch(
      () => this.gameState.spatial.intentionIndicators,
      (indicators) => {
        this._renderIntentionIndicators(indicators);
      },
      { deep: true, immediate: true }
    );

    if (!this.levelConfig) {
      throw new Error('LevelConfig not initialized');
    }

    // Clean up watchers BEFORE Phaser destroys internal objects
    // Use 'destroy' event which fires before 'shutdown'
    this.events.once('destroy', () => {
      // Reset camera tracking to prevent cross-level camera panning
      this.lastPlayerCoords = null;

      if (this.terrainWatcher) {
        this.terrainWatcher();
        this.terrainWatcher = null;
      }
      this.terrainSprites.clear();

      if (this.characterWatcher) {
        this.characterWatcher();
        this.characterWatcher = null;
      }
      if (this.animationWatcher) {
        this.animationWatcher();
        this.animationWatcher = null;
      }
      this.characterSprites.clear();
      this.energyDisplays.clear();
      this.animatingEntities.clear();

      if (this.highlightWatcher) {
        this.highlightWatcher();
        this.highlightWatcher = null;
      }
      if (this.intentionWatcher) {
        this.intentionWatcher();
        this.intentionWatcher = null;
      }
      // Null out groups to prevent further access
      this.playerActionHighlights = null;
      this.enemyPatternHighlights = null;
      this.intentionIndicators = null;
    });

    // Scene is now ready - watchers will catch up to any spatial state that already exists.
    // No need to emit SCENE_READY event - LevelManager doesn't wait for us anymore.
  }

  private _setupInputEvents(): void {
    let downStart = { x: 0, y: 0, time: 0 };

    // Track pointer down position for tap detection
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      downStart.x = pointer.worldX;
      downStart.y = pointer.worldY;
      downStart.time = this.time.now;
      // downSprite is set by SpatialSystem when creating interactive sprites
    });

    // Detect taps (short click with minimal movement)
    this.input.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      const sprite = this.downSprite;
      this.downSprite = null; // Always clear

      if (!sprite) return;

      const dist = Phaser.Math.Distance.Between(
        downStart.x, downStart.y,
        pointer.worldX, pointer.worldY
      );
      const duration = this.time.now - downStart.time;

      // Treat as tap if moved < 5px and held < 200ms
      if (dist < 5 && duration < 200) {
        // Dispatch event with hex coords (not pixels!) - can be consumed by handlers
        this.eventEmitter.dispatch(GameEvents.PLAYER_TAPPED_HEX, {
          hexCoords: sprite.viewCoords.hexCoords,
        });
      }
    });

    // Camera zoom via mouse wheel (handle directly)
    this.input.on('wheel', (
      _pointer: Phaser.Input.Pointer,
      _gameObjects: Phaser.GameObjects.GameObject[],
      _deltaX: number,
      deltaY: number,
      _deltaZ: number
    ) => {
      const newZoom = this.cameras.main.zoom - deltaY * 0.001;
      this.cameras.main.zoom = Phaser.Math.Clamp(newZoom, 0.5, 2.0);
    });

    // Camera pan via drag (handle directly)
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;

      const cam = this.cameras.main;
      const deltaX = pointer.x - pointer.prevPosition.x;
      const deltaY = pointer.y - pointer.prevPosition.y;
      cam.scrollX -= deltaX / cam.zoom;
      cam.scrollY -= deltaY / cam.zoom;
    });
  }

  // Terrain rendering (reactive to spatial state)
  private _renderTerrain(terrain: Array<{ entityId: string; coords: { q: number; r: number }; spriteKey: string }>): void {
    if (!this.viewConverter) return;

    // Clear existing terrain sprites
    for (const sprite of this.terrainSprites.values()) {
      sprite.destroy();
    }
    this.terrainSprites.clear();

    // Create sprites for each terrain tile
    for (const tile of terrain) {
      const coords = HexCoords.get(tile.coords);
      const hvc = new HexViewCoords(coords, this.viewConverter);
      const [x, y] = hvc.getLocation();

      const sprite = this.add.sprite(x, y, tile.spriteKey);
      sprite.setDepth(0); // Terrain on layer 0

      // Scale to fit hex
      const size = this.viewConverter.hexLongRadius * 2;
      sprite.setDisplaySize(size, size);

      // Create hexagonal mask
      const maskPoints = hvc.getLocalPoints({ inset: 1 });
      const maskGraphics = this.add.graphics();
      maskGraphics.fillPoints(maskPoints);
      maskGraphics.setPosition(x, y);
      sprite.setMask(new Phaser.Display.Masks.GeometryMask(this, maskGraphics));

      // Create hexagonal hit area for click handling
      const hitAreaPoints = maskPoints.map(p => ({
        x: sprite.displayOriginX + (p.x / sprite.scaleX),
        y: sprite.displayOriginY + (p.y / sprite.scaleY),
      }));
      const poly = new Phaser.Geom.Polygon(hitAreaPoints);

      sprite.setInteractive({
        hitArea: poly,
        hitAreaCallback: Phaser.Geom.Polygon.Contains,
        useHandCursor: true,
      });

      // Store viewCoords for input handling (needed by InputSystem)
      (sprite as any).viewCoords = { hexCoords: tile.coords };
      sprite.on('pointerdown', () => {
        this.downSprite = sprite as any;
      });

      this.terrainSprites.set(tile.entityId, sprite);
    }

    // Set up camera bounds and zoom based on terrain extent
    if (terrain.length > 0) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;

      for (const sprite of this.terrainSprites.values()) {
        const halfSize = this.viewConverter.hexLongRadius;
        minX = Math.min(minX, sprite.x - halfSize);
        maxX = Math.max(maxX, sprite.x + halfSize);
        minY = Math.min(minY, sprite.y - halfSize);
        maxY = Math.max(maxY, sprite.y + halfSize);
      }

      const paddingX = this.cameras.main.width / 2;
      const paddingY = this.cameras.main.height / 2;

      this.cameras.main.setBounds(
        minX - paddingX,
        minY - paddingY,
        (maxX - minX) + (paddingX * 2),
        (maxY - minY) + (paddingY * 2)
      );

      // Set zoom to fit 5 hexes wide
      const hexWidth = this.viewConverter.hexLongRadius * Math.sqrt(3);
      const targetWidth = 5 * hexWidth;
      const zoomX = this.cameras.main.width / targetWidth;
      this.cameras.main.setZoom(Math.min(zoomX, 2.0));

      // Camera will be centered on player by _updateCharacters when characters load
    }
  }

  // Character rendering (reactive to spatial state)
  private _updateCharacters(characters: UiState['spatial']['characters']): void {
    if (!this.viewConverter) return;

    // Extract player speed for turn dot calculations
    const playerChar = characters.find(c => c.isPlayer);
    if (playerChar?.speed) {
      this.playerSpeed = playerChar.speed;
    }

    const currentIds = new Set(characters.map(c => c.entityId));

    // Remove sprites for entities that no longer exist
    for (const [entityId, sprite] of this.characterSprites.entries()) {
      if (!currentIds.has(entityId)) {
        sprite.destroy();
        this.characterSprites.delete(entityId);

        // Also remove energy display
        const energyDisplay = this.energyDisplays.get(entityId);
        if (energyDisplay) {
          energyDisplay.destroy();
          this.energyDisplays.delete(entityId);
        }
      }
    }

    // Create/update sprites for each character
    for (const character of characters) {
      let sprite = this.characterSprites.get(character.entityId);

      if (!sprite) {
        // Create new sprite
        const coords = HexCoords.get(character.coords);
        const hvc = new HexViewCoords(coords, this.viewConverter);
        const [x, y] = hvc.getLocation();

        sprite = this.add.sprite(x, y, character.spriteKey);
        sprite.setDepth(1); // Characters on layer 1

        const size = (this.viewConverter.hexLongRadius * 2) * character.scale;
        sprite.setDisplaySize(size, size);

        this.characterSprites.set(character.entityId, sprite);
      } else if (sprite.texture.key !== character.spriteKey) {
        // Entity ID reused across levels with a different sprite — update texture
        sprite.setTexture(character.spriteKey);
        const size = (this.viewConverter.hexLongRadius * 2) * character.scale;
        sprite.setDisplaySize(size, size);
      }

      // Update sprite properties (only if not animating)
      if (!this.animatingEntities.has(character.entityId)) {
        const coords = HexCoords.get(character.coords);
        const hvc = new HexViewCoords(coords, this.viewConverter);
        const [x, y] = hvc.getLocation();

        sprite.x = x;
        sprite.y = y;

        if (character.bearing !== null) {
          sprite.angle = this._bearingToAngle(character.bearing);
        }
      }

      // Update alpha based on dead status
      sprite.alpha = character.isDead ? 0 : 1;

      // Update turn indicator dots
      if (character.energy !== null && character.speed !== null && this.playerSpeed > 0) {
        this._updateTurnIndicator(character.entityId, sprite, character.energy, character.speed);
      } else {
        // Remove energy display if no longer needed
        const display = this.energyDisplays.get(character.entityId);
        if (display) {
          display.destroy();
          this.energyDisplays.delete(character.entityId);
        }
      }
    }

    // Track player movement and center/pan camera
    const player = characters.find(c => c.isPlayer);
    if (player) {
      if (this.lastPlayerCoords) {
        // We have a previous position - check if player moved and animate if so
        if (player.coords.q !== this.lastPlayerCoords.q || player.coords.r !== this.lastPlayerCoords.r) {
          // Player moved, pan camera to follow
          const oldHvc = new HexViewCoords(HexCoords.get(this.lastPlayerCoords), this.viewConverter);
          const [oldX, oldY] = oldHvc.getLocation();

          const newHvc = new HexViewCoords(HexCoords.get(player.coords), this.viewConverter);
          const [newX, newY] = newHvc.getLocation();

          const dx = newX - oldX;
          const dy = newY - oldY;

          // Comment out the camera pan to try it without
          // this for a while. Might want to put it back though.
          // this.cameras.main.pan(
          //   this.cameras.main.midPoint.x + dx,
          //   this.cameras.main.midPoint.y + dy,
          //   TUNE.ANIMATION_DURATION * 3,
          //   'Sine.easeInOut'
          // );
        }
      } else {
        // First time seeing player - center immediately without animation
        const coords = HexCoords.get(player.coords);
        const hvc = new HexViewCoords(coords, this.viewConverter);
        const [x, y] = hvc.getLocation();
        this.cameras.main.centerOn(x, y);
      }

      // Update tracked position for next time
      this.lastPlayerCoords = { q: player.coords.q, r: player.coords.r };
    }
  }

  private _updateTurnIndicator(entityId: string, sprite: Phaser.GameObjects.Sprite, currentEnergy: number, actorSpeed: number): void {
    let container = this.energyDisplays.get(entityId);

    if (!container) {
      container = this.add.container(0, 0);
      container.setDepth(2);
      this.energyDisplays.set(entityId, container);
    }

    container.removeAll(true);
    this._drawTurnDots(container, currentEnergy, actorSpeed);

    container.setData('followSprite', sprite);
  }

  // Sync turn indicator positions to their sprites each frame (handles animation movement)
  update(): void {
    for (const container of this.energyDisplays.values()) {
      const sprite = container.getData('followSprite') as Phaser.GameObjects.Sprite | undefined;
      if (sprite) {
        container.x = sprite.x;
        container.y = sprite.y + 42;
      }
    }
  }

  private _drawTurnDots(container: Phaser.GameObjects.Container, currentEnergy: number, actorSpeed: number): void {
    const NUM_DOTS = 4;
    const CURRENT_RADIUS = 5;
    const FUTURE_RADIUS = 3;
    const GAP_AFTER_CURRENT = 5;
    const PADDING = 5;
    const FILL_COLOR = 0xf0e6d0;
    const CURRENT_FILL_ALPHA = 1.0;
    const FUTURE_FILL_ALPHA = 0.75;
    const EMPTY_ALPHA = 0.12;
    const BORDER_COLOR = 0xf0e6d0;
    const INNER_RING_COLOR = 0xf0e6d0;

    const dots = computeTurnDots(currentEnergy, actorSpeed, this.playerSpeed, NUM_DOTS);

    // Calculate total width for centering
    const totalWidth = (CURRENT_RADIUS * 2) + GAP_AFTER_CURRENT +
      ((NUM_DOTS - 1) * FUTURE_RADIUS * 2) + ((NUM_DOTS - 2) * PADDING);
    let x = -totalWidth / 2;

    for (let i = 0; i < NUM_DOTS; i++) {
      const radius = i === 0 ? CURRENT_RADIUS : FUTURE_RADIUS;
      const actions = dots[i];

      const dotX = x + radius;

      const gfx = this.add.graphics();

      // Fill: full opacity for current dot, slightly transparent for future, faint for skipping
      if (actions > 0) {
        gfx.fillStyle(FILL_COLOR, i === 0 ? CURRENT_FILL_ALPHA : FUTURE_FILL_ALPHA);
      } else {
        gfx.fillStyle(FILL_COLOR, EMPTY_ALPHA);
      }
      gfx.fillCircle(dotX, 0, radius);

      // Double-action indicator: inner concentric ring
      if (actions >= 2) {
        gfx.lineStyle(0.75, INNER_RING_COLOR, 0.8);
        gfx.strokeCircle(dotX, 0, radius * 0.55);
      }

      // Border: only on empty dots
      if (actions === 0) {
        gfx.lineStyle(0.75, BORDER_COLOR, 0.9);
        gfx.strokeCircle(dotX, 0, radius);
      }

      container.add(gfx);

      x += radius * 2 + (i === 0 ? GAP_AFTER_CURRENT : PADDING);
    }
  }

  // Animation playback (reactive to spatial state)
  private async _playAnimations(plan: any[][]): Promise<void> {
    if (!plan || plan.length === 0) {
      this.eventEmitter.emit(GameEvents.ANIMATIONS_COMPLETE);
      return;
    }

    for (const stage of plan) {
      const stagePromises: Promise<void>[] = [];

      for (const request of stage) {
        switch (request.type) {
          case 'MOVE': {
            const sprite = this.characterSprites.get(request.entityId);
            if (!sprite) continue;

            this.animatingEntities.add(request.entityId);
            const coords = HexCoords.get(request.targetCoords);
            const hvc = new HexViewCoords(coords, this.viewConverter);
            const [targetX, targetY] = hvc.getLocation();

            const tweenPromise = new Promise<void>((resolve) => {
              this.tweens.add({
                targets: sprite,
                x: targetX,
                y: targetY,
                duration: 150, // TUNE.ANIMATION_DURATION
                ease: 'Sine.easeInOut',
                onComplete: () => {
                  this.animatingEntities.delete(request.entityId);
                  resolve();
                }
              });
            });
            stagePromises.push(tweenPromise);
            break;
          }

          case 'ATTACK_JAB': {
            const sprite = this.characterSprites.get(request.entityId);
            if (!sprite) continue;

            this.animatingEntities.add(request.entityId);
            const coords = HexCoords.get(request.targetCoords);
            const hvc = new HexViewCoords(coords, this.viewConverter);
            const [targetX, targetY] = hvc.getLocation();

            const jabDestX = sprite.x + (targetX - sprite.x) / 2;
            const jabDestY = sprite.y + (targetY - sprite.y) / 2;

            const tweenPromise = new Promise<void>((resolve) => {
              this.tweens.add({
                targets: sprite,
                x: jabDestX,
                y: jabDestY,
                duration: 75,
                yoyo: true,
                ease: 'Sine.easeInOut',
                onComplete: () => {
                  this.animatingEntities.delete(request.entityId);
                  resolve();
                }
              });
            });
            stagePromises.push(tweenPromise);
            break;
          }

          case 'TURN': {
            const sprite = this.characterSprites.get(request.entityId);
            if (!sprite) continue;

            this.animatingEntities.add(request.entityId);
            const targetAngle = this._bearingToAngle(request.bearing);
            const shortestAngle = Phaser.Math.Angle.ShortestBetween(sprite.angle, targetAngle);

            const tweenPromise = new Promise<void>((resolve) => {
              this.tweens.add({
                targets: sprite,
                angle: sprite.angle + shortestAngle,
                duration: 150,
                ease: 'Sine.easeInOut',
                onComplete: () => {
                  this.animatingEntities.delete(request.entityId);
                  resolve();
                }
              });
            });
            stagePromises.push(tweenPromise);
            break;
          }

          case 'MOVE_SWIPE': {
            const sprite = this.characterSprites.get(request.entityId);
            if (!sprite) continue;

            this.animatingEntities.add(request.entityId);
            const startPos = { x: sprite.x, y: sprite.y };

            const finalCoords = HexCoords.get(request.targetCoords);
            const finalHvc = new HexViewCoords(finalCoords, this.viewConverter);
            const [finalX, finalY] = finalHvc.getLocation();

            const swipeCoords = HexCoords.get(request.swipeCoords);
            const swipeHvc = new HexViewCoords(swipeCoords, this.viewConverter);
            const [swipeX, swipeY] = swipeHvc.getLocation();

            const controlPoint = {
              x: startPos.x + (swipeX - startPos.x) * 0.5,
              y: startPos.y + (swipeY - startPos.y) * 0.5
            };

            const path = new Phaser.Curves.Path(startPos.x, startPos.y)
              .quadraticBezierTo(finalX, finalY, controlPoint.x, controlPoint.y);

            const tweenPromise = new Promise<void>((resolve) => {
              const tweenTarget = { t: 0 };
              this.tweens.add({
                targets: tweenTarget,
                t: 1,
                duration: 150,
                ease: 'Sine.easeInOut',
                onUpdate: () => {
                  const positionOnPath = path.getPoint(tweenTarget.t);
                  sprite.x = positionOnPath.x;
                  sprite.y = positionOnPath.y;
                },
                onComplete: () => {
                  this.animatingEntities.delete(request.entityId);
                  resolve();
                }
              });
            });
            stagePromises.push(tweenPromise);
            break;
          }

          case 'DEATH': {
            const sprite = this.characterSprites.get(request.entityId);
            if (!sprite) continue;

            this.animatingEntities.add(request.entityId);
            const tweenPromise = new Promise<void>((resolve) => {
              this.tweens.add({
                targets: sprite,
                alpha: 0,
                duration: 225,
                ease: 'Sine.easeInOut',
                onComplete: () => {
                  this.animatingEntities.delete(request.entityId);
                  resolve();
                }
              });
            });
            stagePromises.push(tweenPromise);
            break;
          }

          case 'PROJECTILE': {
            if (!request.sourceCoords || !request.targetCoords) continue;

            const startCoords = HexCoords.get(request.sourceCoords);
            const startHvc = new HexViewCoords(startCoords, this.viewConverter);
            const [startX, startY] = startHvc.getLocation();

            const endCoords = HexCoords.get(request.targetCoords);
            const endHvc = new HexViewCoords(endCoords, this.viewConverter);
            const [endX, endY] = endHvc.getLocation();

            const projectile = this.add.graphics();
            projectile.fillStyle(0xd4a843, 1);
            projectile.fillCircle(0, 0, 10);
            projectile.setPosition(startX, startY);
            projectile.setDepth(3);

            const controlPoint = {
              x: (startX + endX) / 2,
              y: (startY + endY) / 2 - this.viewConverter.hexLongRadius
            };

            const path = new Phaser.Curves.Path(startX, startY)
              .quadraticBezierTo(endX, endY, controlPoint.x, controlPoint.y);

            const tweenPromise = new Promise<void>((resolve) => {
              const tweenTarget = { t: 0 };
              this.tweens.add({
                targets: tweenTarget,
                t: 1,
                duration: 300,
                ease: 'Linear',
                onUpdate: () => {
                  const positionOnPath = path.getPoint(tweenTarget.t);
                  projectile.x = positionOnPath.x;
                  projectile.y = positionOnPath.y;
                },
                onComplete: () => {
                  projectile.destroy();
                  resolve();
                }
              });
            });
            stagePromises.push(tweenPromise);
            break;
          }

          default:
            console.warn(`[LevelScene] Unknown animation type: ${request.type}`);
            break;
        }
      }

      await Promise.all(stagePromises);
    }

    // All animations complete - emit event to trigger resolve callback
    this.eventEmitter.emit(GameEvents.ANIMATIONS_COMPLETE);
  }

  // Highlight rendering (reactive to spatial state)
  private _renderPlayerActionHighlights(hexCoords: Array<{ q: number; r: number }>): void {
    this._renderHighlights(this.playerActionHighlights, hexCoords, 0xffff00, 0.1, 0.4);
  }

  private _renderEnemyPatternHighlights(hexCoords: Array<{ q: number; r: number }> | null): void {
    this._renderHighlights(this.enemyPatternHighlights, hexCoords, 0xc85040, 0.25, 0.6);
  }

  /**
   * Generic highlight rendering method
   * @param group - Phaser group to add highlights to
   * @param hexCoords - Array of hex coordinates to highlight
   * @param color - Hex color value
   * @param fillAlpha - Alpha for fill
   * @param lineAlpha - Alpha for stroke
   */
  private _renderHighlights(
    group: Phaser.GameObjects.Group | null,
    hexCoords: Array<{ q: number; r: number }> | null,
    color: number,
    fillAlpha: number,
    lineAlpha: number
  ): void {
    if (!group || !this.viewConverter) return;

    // Clear existing highlights
    group.clear(true, true);

    if (!hexCoords || hexCoords.length === 0) return;

    // Create highlight graphics for each coordinate
    for (const coord of hexCoords) {
      this._renderHighlight(group, coord, color, fillAlpha, lineAlpha);
    }
  }

  /**
   * Render a single highlight graphic
   */
  private _renderHighlight(
    group: Phaser.GameObjects.Group,
    coord: { q: number; r: number },
    color: number,
    fillAlpha: number,
    lineAlpha: number,
    lineColor?: number
  ): void {
    const coords = HexCoords.get(coord);
    const hvc = new HexViewCoords(coords, this.viewConverter);
    const loc = hvc.getLocation();
    const localPoints = hvc.getLocalPoints({ inset: 3 });

    const graphics = this.add.graphics({ x: loc[0], y: loc[1] });
    graphics.fillStyle(color, fillAlpha);
    graphics.lineStyle(2, lineColor ?? color, lineAlpha);
    graphics.fillPoints(localPoints, true);
    graphics.strokePoints(localPoints, true);
    graphics.setDepth(0.5); // Between terrain and actors

    group.add(graphics);
  }

  /**
   * Render intention indicators showing what enemies plan to do
   */
  private _renderIntentionIndicators(
    indicators: Array<{
      entityId: string;
      position: { q: number; r: number };
      bearing: number;
      actionType: string;
      details: {
        coords?: { q: number; r: number };
        bearing?: number;
        targetId?: string;
        sourceComponentName?: string;
      };
    }>
  ): void {
    if (!this.intentionIndicators || !this.viewConverter) return;

    // Clear existing indicators
    this.intentionIndicators.clear(true, true);

    if (!indicators || indicators.length === 0) return;

    // Render each indicator based on action type
    for (const indicator of indicators) {
      this._renderIntentionIndicator(indicator);
    }
  }

  /**
   * Render a single intention indicator
   */
  private _renderIntentionIndicator(indicator: {
    entityId: string;
    position: { q: number; r: number };
    bearing: number;
    actionType: string;
    details: {
      coords?: { q: number; r: number };
      bearing?: number;
      targetId?: string;
      sourceComponentName?: string;
    };
  }): void {
    switch (indicator.actionType) {
      case 'MOVE': {
        // Teal/verdigris highlight on both source and destination for movement
        const targetCoords = indicator.details.coords;
        if (targetCoords) {
          this._renderHighlight(this.intentionIndicators!, indicator.position, 0x3a80b0, 0.3, 0.75, 0x80d0ff); // Source hex
          this._renderHighlight(this.intentionIndicators!, targetCoords, 0x3a80b0, 0.2, 0.65, 0x80d0ff); // Destination hex

          // Find which bearing leads to the target
          const bearing = HexCoords.get(indicator.position).bearingTo(HexCoords.get(targetCoords));
          if (bearing) {
            this._drawChevron(indicator.position, bearing, 0x80d0ff, 8, true);
          }
        }
        break;
      }

      case 'TURN': {
        // Teal/verdigris highlight on source, chevron showing new bearing
        const targetBearing = indicator.details.bearing;
        if (targetBearing !== undefined) {
          this._renderHighlight(this.intentionIndicators!, indicator.position, 0x3a80b0, 0.3, 0.75, 0x80d0ff); // Source hex
          this._drawChevron(indicator.position, targetBearing, 0x80d0ff, 2, false);
        }
        break;
      }

      case 'RANGED_ATTACK': {
        // Orange highlight on both source and destination for ranged attack
        // Note: We don't have access to the full entity manager here, so we can't look up target position
        // This would need to be included in the indicator data if we want to show ranged attack indicators
        if (indicator.details.coords) {
          this._renderHighlight(this.intentionIndicators!, indicator.position, 0xe8943c, 0.3, 0.5); // Source hex
          this._renderHighlight(this.intentionIndicators!, indicator.details.coords, 0xe8943c, 0.2, 0.4); // Destination hex
          this._drawArcLine(indicator.position, indicator.details.coords, 0xe8943c, 2);
        }
        break;
      }

      case 'IDLE': {
        // Special case: OmniBite attacks all adjacent hexes, but display as if targeting the player
        if (indicator.details.sourceComponentName === 'OmniBite') {
          // Find the player position
          const player = this.gameState.spatial.characters.find(c => c.isPlayer);
          if (player) {
            // Check if player is adjacent to OmniBite
            const sourceCoords = HexCoords.get(indicator.position);
            const playerCoords = HexCoords.get(player.coords);
            const isAdjacent = !!sourceCoords.isNeighbor(playerCoords);

            if (isAdjacent) {
              // Display as if targeting the player
              this._renderHighlight(this.intentionIndicators!, indicator.position, 0xe8943c, 0.3, 0.5); // Source hex
              this._renderHighlight(this.intentionIndicators!, player.coords, 0xe8943c, 0.2, 0.4); // Player hex

              // Draw chevron pointing at player
              const bearing = sourceCoords.bearingTo(playerCoords);
              if (bearing) {
                this._drawChevron(indicator.position, bearing, 0xe8943c, 8, true);
              }
            }
          }
        } else {
          // Regular bite attack with a specific target
          const targetCoords = indicator.details.coords;
          if (targetCoords) {
            this._renderHighlight(this.intentionIndicators!, indicator.position, 0xe8943c, 0.3, 0.5); // Source hex
            this._renderHighlight(this.intentionIndicators!, targetCoords, 0xe8943c, 0.2, 0.4); // Destination hex

            // Find which bearing leads to the target
            const bearing = HexCoords.get(indicator.position).bearingTo(HexCoords.get(targetCoords));
            if (bearing) {
              this._drawChevron(indicator.position, bearing, 0xe8943c, 8, true);
            }
          }
        }
        break;
      }
    }
  }

  /**
   * Draw a chevron (arrow) indicator at a hex position
   */
  private _drawChevron(
    coords: { q: number; r: number },
    bearing: number,
    color: number,
    distanceOutsideHex: number,
    double: boolean
  ): void {
    const hvc = new HexViewCoords(coords, this.viewConverter);
    const [centerX, centerY] = hvc.getLocation();

    // Get the angle for this bearing
    const angle = this._bearingToAngle(bearing);
    const angleRad = Phaser.Math.DegToRad(angle - 90); // Adjust for Phaser's coordinate system

    // Position the chevron just outside the hex edge
    const distance = this.viewConverter.hexLongRadius + distanceOutsideHex;
    const chevronX = centerX + Math.cos(angleRad) * distance;
    const chevronY = centerY + Math.sin(angleRad) * distance;

    const graphics = this.add.graphics();
    graphics.lineStyle(4, color, 0.9); // Bold line

    // Chevron dimensions
    const chevronSize = 16; // Length of each chevron arm
    const chevronSpread = 120; // Angle spread of the chevron (degrees)
    const chevronGap = 8; // Gap between the two chevrons

    // Draw two chevrons
    let num = 1;
    if (double) num = 2;
    for (let i = 0; i < num; i++) {
      const offsetDist = i * chevronGap;
      const baseX = chevronX + Math.cos(angleRad) * offsetDist;
      const baseY = chevronY + Math.sin(angleRad) * offsetDist;

      // Calculate the two arms of the chevron
      const topArmAngle = angleRad - Phaser.Math.DegToRad(chevronSpread / 2);
      const bottomArmAngle = angleRad + Phaser.Math.DegToRad(chevronSpread / 2);

      const topArmX = baseX - Math.cos(topArmAngle) * chevronSize;
      const topArmY = baseY - Math.sin(topArmAngle) * chevronSize;

      const bottomArmX = baseX - Math.cos(bottomArmAngle) * chevronSize;
      const bottomArmY = baseY - Math.sin(bottomArmAngle) * chevronSize;

      // Draw the two lines forming the chevron
      graphics.lineBetween(topArmX, topArmY, baseX, baseY);
      graphics.lineBetween(baseX, baseY, bottomArmX, bottomArmY);
    }

    graphics.setDepth(0.6);
    this.intentionIndicators!.add(graphics);
  }

  private _drawArcLine(
    sourceCoords: { q: number; r: number },
    targetCoords: { q: number; r: number },
    color: number,
    width: number) {
    const sourceHvc = new HexViewCoords(sourceCoords, this.viewConverter);
    const [sourceX, sourceY] = sourceHvc.getLocation();
    const targetHvc = new HexViewCoords(targetCoords, this.viewConverter);
    const [targetX, targetY] = targetHvc.getLocation();

    // Control point is above the midpoint, like the projectile animation
    const controlPoint = {
      x: (sourceX + targetX) / 2,
      y: (sourceY + targetY) / 2 - this.viewConverter.hexLongRadius
    };

    const graphics = this.add.graphics();
    graphics.lineStyle(width, color, 0.8);

    // Draw quadratic bezier curve
    const path = new Phaser.Curves.Path(sourceX, sourceY)
      .quadraticBezierTo(targetX, targetY, controlPoint.x, controlPoint.y);
    path.draw(graphics);

    graphics.setDepth(2.5);
    this.intentionIndicators!.add(graphics);
  }

  _bearingToAngle(bearing: number) {
    switch (bearing) {
      case HexBearing.Q: return 90;
      case HexBearing.R: return 150;
      case HexBearing.S: return 210;
      case HexBearing._Q: return 270;
      case HexBearing._R: return 330;
      case HexBearing._S: return 30;
      default: return 0;
    }
  }
}
